import React from 'react';
import { useTranslation } from 'react-i18next';
import { Handle, Position, type NodeProps } from '@xyflow/react';

import { Field, type SelectOption } from '@/nova/components';

import { aspectLabel } from '../../../aspect-ratios';
import { StudioSelect } from '../StudioSelect';
import { formatVersionTimestamp } from '../../../storyboard';
import { isVideoAspect } from '../../../video-composer';
import { VideoResult } from '../../VideoResult';
import { useCanvasActions } from '../context';
import type { OutputFlowNode } from '../graph';
import { Card, HeaderRow, NoDrag, OutputVideo, Pill, Summary } from './nodes.styles';

/**
 * The terminal node: the finished ad's aspect ratio, its call to action (rendered by the model into the
 * closing outro beat), a read-only summary, AND the finished ad itself once rendered. The rendered mp4 plays
 * inline in this card; when the ad has been regenerated, a version picker over the render history lets the
 * owner switch between (and re-download) earlier renders. Newest is selected by default.
 */
export const OutputNode = ({ data }: NodeProps<OutputFlowNode>): React.ReactElement => {
  const { t } = useTranslation();
  const actions = useCanvasActions();

  // The ratios the chosen model can produce (threaded from the composer via the node data); labelled with a
  // best-fit platform. Falls back to the two universal ratios if absent.
  const aspectOptions: SelectOption[] = (data.aspects ?? ['16:9', '9:16']).map((a) => ({
    value: a,
    label: aspectLabel(a),
  }));

  const onAspectChange = (value: string): void => {
    if (isVideoAspect(value)) actions.setAspect(value);
  };

  const versions = data.versions ?? [];
  const hasAny = versions.length > 0;
  // The selected version (by key), defaulting to the newest (versions are newest-first). It may be a
  // storyboard-only version (no url) that has not been rendered yet.
  const selected = versions.find((v) => v.key === data.selectedVersionKey) ?? versions[0];
  const selectedUrl = selected?.url;
  const versionOptions: SelectOption[] = versions.map((v, i) => ({
    value: v.key,
    // Storyboard-only versions are flagged; the newest render is "Latest"; the rest show their timestamp.
    label: !v.url
      ? t('Storyboard, no video · {{when}}', { when: formatVersionTimestamp(v.createdAt) })
      : i === 0
        ? t('Latest · {{when}}', { when: formatVersionTimestamp(v.createdAt) })
        : formatVersionTimestamp(v.createdAt),
  }));

  return (
    <Card $width={hasAny ? 300 : 240} $status={data.status}>
      <Handle type="target" position={Position.Left} />
      <HeaderRow>
        <Pill text="Finished ad" />
      </HeaderRow>
      <NoDrag>
        <StudioSelect label={t('Aspect ratio')} value={data.aspect} onChange={onAspectChange} options={aspectOptions} />
        <Field label={t('Call to action')} value={data.cta} onChange={actions.setCta} />
      </NoDrag>
      <Summary text="{{beats}} beats · {{seconds}}s" placeholders={{ beats: data.beats, seconds: data.seconds }} />
      {hasAny ? (
        <OutputVideo>
          {versions.length > 1 && selected ? (
            <NoDrag>
              <StudioSelect
                label={t('Version')}
                value={selected.key}
                onChange={actions.selectVersion}
                options={versionOptions}
              />
            </NoDrag>
          ) : null}
          {selectedUrl ? (
            // key by url so switching versions remounts the player (reloads + resets play state); no shared
            // poster so each version previews its OWN first frame (data.poster is the last render's outro frame).
            <VideoResult
              key={selectedUrl}
              url={selectedUrl}
              aspect={data.aspect}
              filename={data.downloadName || 'video-ad.mp4'}
            />
          ) : (
            <Summary text="No video for this version yet. Generate it to render this storyboard." />
          )}
        </OutputVideo>
      ) : null}
    </Card>
  );
};

export default OutputNode;
